import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";

import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";

import { IBreadcrumbsAndMenu, ITab } from "../../../types/global";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  salesOrderSchema,
  salesOrderValidation,
} from "../../../components/Form/Sales/Order/schema";
import {
  ActivityLogDocumentType,
  SalesOrderFindUniqueQuery,
  useSalesOrderFindUniqueQuery,
} from "../../../generated/sales";
import { ActivityLogSlideInPanel } from "../../../components/UI/SlideInPanel/ActivityLogSlideInPanel";
import { CustomizedTooltip } from "../../../components/Custom/CustomizedTooltip";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import DocumentInfoTab from "./DocumentInfoTab";
import PurchaseRequestTab from "./PurchaseRequestTab";
import CashSalesTab from "./CashSalesTab";
import SalesInvoiceTab from "./SalesInvoiceTab";
import DeliveryOrderTab from "./DeliveryOrderTab";
import SalesReturnTab from "./SalesReturnTab";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useStateContext } from "../../../contexts/auth-context";

const SalesOrderContainer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const {
    state: { authUser, permissions },
  } = useStateContext();
  const navigate = useNavigate();

  const tabs: ITab[] = [
    {
      label: t("sales.document_info"),
      path: `${pathname}`,
    },
    {
      label: t("purchase.request.index"),
      path: `${pathname}?tab=purchase_request`,
    },
    {
      label: t("sales.cash_sales.index"),
      path: `${pathname}?tab=cash_sales`,
    },
    {
      label: t("sales.invoice.index"),
      path: `${pathname}?tab=sales_invoice`,
    },
    {
      label: t("logistic.delivery_order.index"),
      path: `${pathname}?tab=delivery_order`,
    },
    {
      label: t("sales.return.index"),
      path: `${pathname}?tab=sales_return`,
    },
  ];

  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const { id } = useParams();

  const { data, isLoading, isSuccess } =
    useSalesOrderFindUniqueQuery<SalesOrderFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    if (isSuccess && permissions?.sales_order?.view_self) {
      const createdBy = data?.SalesOrderFindUnique?.created_by;
      const relatedUserList = data?.SalesOrderFindUnique?.related_user_list;
      if (
        createdBy?.user_unique_id === authUser?.unique_id ||
        relatedUserList?.find(
          (user) => user.user_unique_id === authUser?.unique_id
        )
      ) {
        return;
      } else {
        navigate(`/unauthorized`, {
          replace: true,
        });
      }
    }
  }, [
    authUser?.unique_id,
    data?.SalesOrderFindUnique?.created_by,
    data?.SalesOrderFindUnique?.related_user_list,
    isSuccess,
    navigate,
    permissions?.sales_order?.view_self,
  ]);

  const renderTab = (tab: string | null) => {
    switch (tab) {
      case "purchase_request":
        return (
          <PurchaseRequestTab
            referenceUniqueId={data?.SalesOrderFindUnique.unique_id}
          />
        );
      case "cash_sales":
        return (
          <CashSalesTab
            referenceUniqueId={data?.SalesOrderFindUnique.unique_id}
          />
        );
      case "sales_invoice":
        return (
          <SalesInvoiceTab
            referenceUniqueId={data?.SalesOrderFindUnique.unique_id}
          />
        );
      case "delivery_order":
        return (
          <DeliveryOrderTab
            referenceUniqueId={data?.SalesOrderFindUnique.unique_id}
          />
        );
      case "sales_return":
        return (
          <SalesReturnTab
            referenceUniqueId={data?.SalesOrderFindUnique.unique_id}
          />
        );
      default:
        return <DocumentInfoTab />;
    }
  };

  const currentTab = pathname + (tab ? `?tab=${tab}` : "");

  const branch_id = sessionStorage.getItem("branch-id");

  const methods = useForm<any>({
    defaultValues: {
      ...salesOrderSchema,
      branch_id: branch_id ? parseInt(branch_id) : undefined,
    },
    resolver: yupResolver<any>(salesOrderValidation),
  });

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("sales.index"),
      to: "/sales",
    },
    {
      name: t("sales.order.index"),
      to: "/sales/order",
    },
    {
      name: id
        ? data?.SalesOrderFindUnique.unique_id || ""
        : t("button.create") + t("sales.order.index"),
    },
  ];

  const status = data?.SalesOrderFindUnique.aggrid_status;

  if (id && isLoading) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box display={"flex"} justifyContent={"space-between"} maxWidth={1040}>
        <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
        {id && (
          <CustomizedTooltip title="ดูความเคลื่อนไหว" enterNextDelay={200}>
            <IconButton
              onClick={() => {
                setOpenDrawer(true);
              }}
              sx={{
                color: (theme) => theme.palette.primary.main,
                padding: 0,
                marginLeft: "4px",
              }}
            >
              <RestoreOutlinedIcon />
            </IconButton>
          </CustomizedTooltip>
        )}
      </Box>
      {(status === "approved" || status === "finished") && (
        <CustomizedTab tabs={tabs} currentTab={currentTab} />
      )}
      {renderTab(tab)}
      {id && (
        <ActivityLogSlideInPanel
          open={openDrawer}
          handleClose={() => setOpenDrawer(false)}
          documentId={id!}
          documentType={ActivityLogDocumentType.SalesOrder}
        />
      )}
    </FormProvider>
  );
};

export default SalesOrderContainer;
